<template>
  <div
    class="tablet:flex tablet:items-center tablet:justify-between desktop:block"
  >
    <div class="mb-3 tablet:mb-0 tablet:flex-1 desktop:mb-8">
      <div class="flex items-center desktop:mb-1">
        <p class="text-2xl font-bold" :class="isFreeTrial && 'text-orange-400'">
          {{ priceText }}
        </p>
        <button
          v-if="isShowLoginToSeePrice"
          class="ml-3 h-6 w-6 text-blue-400"
          :title="t('login-to-see-price')"
          @click="handleLogin"
        >
          <IconViewOff class="h-full w-full" />
        </button>
        <div
          v-if="isShowFloatPriceBadge"
          class="flex h-6 w-6 items-center justify-center text-red-400"
        >
          <IconCritical class="h-5 w-5" />
        </div>
      </div>

      <p class="text-sm text-gray-600">
        {{ perLicenseText }}
      </p>

      <p v-if="isShowLoginToSeePrice" class="text-sm text-gray-600">
        {{ t('login-to-see-price') }}
      </p>
    </div>
    <div class="tablet:basis-[180px]">
      <TheButton
        :id="contextAcnTrackingID"
        :type="buttonType"
        :title="buttonText"
        class="w-full tablet:w-[180px] desktop:w-full"
        @click="handleClick"
      >
        <span class="text-sm">{{ buttonText }}</span>
      </TheButton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    EnumBillingCycle,
    EnumLicenseDeliveryMethod,
    EnumServiceType,
  } from '@/assets/types/api/Products';
  import type { IPricePlan } from '@/stores/pricing/pricingStore';

  interface IProps {
    planData: IPricePlan;
  }

  const props = withDefaults(defineProps<IProps>(), {
    planData: () => {
      return {
        name: '',
        partNumber: '',
        description: '',
        price: null,
        currency: '',
        billingCycleId: EnumBillingCycle.ONE_TIME_PURCHASE,
        licenseDeliveryMethodId: EnumLicenseDeliveryMethod.EMAIL_LICENSE,
        isMostPopular: false,
        isValidRegion: false,
        isShowPrice: false,
        isAccountPrice: false,
        serviceType: EnumServiceType.CLOUD_ENSAAS,
      };
    },
  });

  const route = useRoute();
  const router = useRouter();
  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { consoleDomain } = useWebsite();
  const { isCN } = useLanguage();
  const profileStore = useProfileStore();
  const pricingStore = usePricingStore();
  const { isLogin } = storeToRefs(profileStore);
  const { dataCenter } = storeToRefs(pricingStore);

  const isFreeTrial = computed((): boolean => {
    // 是否為 Free Trial 料號
    return props.planData.billingCycleId === EnumBillingCycle.FREE_TRIAL;
  });

  const isContactSales = computed((): boolean => {
    // 是否為 Contact Sales 料號
    return props.planData.billingCycleId === EnumBillingCycle.CONTACT_SALES;
  });

  const isShowLoginToSeePrice = computed((): boolean => {
    // 是否顯示「登入顯示價格」的文案
    return !isLogin.value && !isFreeTrial.value && !isContactSales.value;
  });

  const isShowFloatPriceBadge = computed((): boolean => {
    // 如果是 Floating Price 就顯示紅色驚嘆號圖示，未登入不顯示

    const { billingCycleId, serviceType } = props.planData;
    return (
      isLogin.value &&
      billingCycleId === EnumBillingCycle.ONE_TIME_PURCHASE &&
      serviceType === EnumServiceType.LICENSE_UPGRADE
    );
  });

  /**
   * * NOTE: 按鈕行為對應表
   * @see https://advantech-curation.visualstudio.com/Marketplace/_wiki/wikis/Marketplace.wiki/1671/Pricing-Plan
   */

  // 根據「按鈕行為對應表」判斷觸發條件類型

  const conditionType = computed((): string => {
    const { billingCycleId, licenseDeliveryMethodId, serviceType } =
      props.planData;

    const {
      FREE_TRIAL,
      CONTACT_SALES,
      SUBSCRIPTION_PAYMENT,
      ONE_TIME_PURCHASE,
    } = EnumBillingCycle;

    const { EMAIL_LICENSE } = EnumLicenseDeliveryMethod;
    const { CLOUD_ENSAAS, LICENSE_UPGRADE } = EnumServiceType;

    if (
      billingCycleId === FREE_TRIAL &&
      licenseDeliveryMethodId === EMAIL_LICENSE
    ) {
      // Billing cycle = Free trial 且 License delivery type = Email
      return 'A';
    } else if (
      billingCycleId === FREE_TRIAL &&
      licenseDeliveryMethodId !== EMAIL_LICENSE &&
      serviceType !== CLOUD_ENSAAS
    ) {
      // Billing cycle = Free trial 且 License delivery type ≠ Email
      return 'B';
    } else if (
      billingCycleId === FREE_TRIAL &&
      licenseDeliveryMethodId !== EMAIL_LICENSE &&
      serviceType === CLOUD_ENSAAS
    ) {
      // Billing cycle = Free trial 且 License delivery type ≠ Email 且 Service type = Cloud (EnSaaS)
      return 'C';
    } else if (billingCycleId === CONTACT_SALES) {
      // Billing cycle = Contact sales
      return 'D';
    } else if (
      billingCycleId === SUBSCRIPTION_PAYMENT &&
      serviceType === CLOUD_ENSAAS
    ) {
      // Billing cycle = Subscription payment 且 Service type = Cloud (EnSaaS)
      return 'E';
    } else if (
      billingCycleId === SUBSCRIPTION_PAYMENT &&
      serviceType !== CLOUD_ENSAAS
    ) {
      // Billing cycle = Subscription payment 且 Service type ≠ Cloud (EnSaaS)
      return 'F';
    } else if (
      billingCycleId === ONE_TIME_PURCHASE &&
      serviceType === LICENSE_UPGRADE
    ) {
      // Billing cycle = One-time purchase 且 Service type = License Upgrade
      return 'G';
    } else if (
      billingCycleId === ONE_TIME_PURCHASE &&
      serviceType !== LICENSE_UPGRADE
    ) {
      // Billing cycle = One-time purchase 且 Service type ≠ License Upgrade
      return 'H';
    } else {
      return '';
    }
  });

  // 根據「按鈕行為對應表」顯示價格文字

  const priceText = computed((): string => {
    const { price, currency, isShowPrice } = props.planData;

    const hiddenPrice = `${currency} ***`;
    const formatPrice = `${currency} ${formatFinanceNumber(price || 0)}`;
    const FREE_TRIAL = t('free-trial');
    const FLOAT_PRICE = t('floating-price');

    switch (conditionType.value) {
      case 'A':
      case 'B':
      case 'C':
        return FREE_TRIAL;
      case 'D':
        return '';
      case 'E':
        return isLogin.value ? formatPrice : hiddenPrice;
      case 'F':
      case 'H':
        return isLogin.value ? (isShowPrice ? formatPrice : '') : hiddenPrice;
      case 'G':
        return isLogin.value ? (isShowPrice ? FLOAT_PRICE : '') : hiddenPrice;
      default:
        return '';
    }
  });

  // 特定料號綁定的方案顯示「per year」
  const PER_YEAR_PART_NUMBERS = ['32WSCSIFIS1201', '32WSCSIFIS1301'];

  const isPerYearPartNumber = computed((): boolean => {
    return PER_YEAR_PART_NUMBERS.includes(props.planData.partNumber);
  });

  // 根據「按鈕行為對應表」顯示價格單位文字

  const perLicenseText = computed((): string => {
    const { isShowPrice } = props.planData;

    switch (conditionType.value) {
      case 'A':
      case 'B':
      case 'C':
      case 'D':
        return '';
      case 'E':
        return isPerYearPartNumber.value ? t('per-year') : t('per-month');
      case 'F':
        return isLogin.value && !isShowPrice
          ? ''
          : isPerYearPartNumber.value
            ? t('per-year')
            : t('per-month');
      case 'G':
      case 'H':
        return isLogin.value && !isShowPrice ? '' : t('per-license');
      default:
        return '';
    }
  });

  // 根據「按鈕行為對應表」顯示按鈕文字

  const buttonText = computed((): string => {
    const { isShowPrice } = props.planData;
    const GET_STARTED = t('get-started');
    const SUBSCRIBE = t('subscribe');
    const PURCHASE = t('purchase');
    const CONTACT_SALES = tGlobal('pricing.contact-sales');

    switch (conditionType.value) {
      case 'A':
      case 'B':
      case 'C':
        return GET_STARTED;
      case 'D':
        return CONTACT_SALES;
      case 'E':
        return SUBSCRIBE;
      case 'F':
        return isLogin.value && !isShowPrice ? CONTACT_SALES : SUBSCRIBE;
      case 'G':
      case 'H':
        return isLogin.value && !isShowPrice ? CONTACT_SALES : PURCHASE;
      default:
        return '';
    }
  });

  // 按鈕樣式

  const buttonType = computed((): 'orange' | 'outline' | 'primary' => {
    const { isShowPrice } = props.planData;

    if (isFreeTrial.value) {
      return 'orange';
    }
    if (isContactSales.value) {
      return 'outline';
    }
    if (isLogin.value && !isShowPrice) {
      return 'outline';
    }
    return 'primary';
  });

  // ACN 追蹤碼

  const contextAcnTrackingID = computed((): string | undefined => {
    const { partNumber } = props.planData;
    if (isCN.value) {
      if (isFreeTrial.value) {
        return 'tracking_FreeTrialBTN';
      }
      return `tracking_redeemBTN_${partNumber}`;
    }
    return undefined;
  });

  const handleLogin = (): void => {
    if (import.meta.client) {
      const callbackUrl = window.location.href;
      profileStore.HANDLE_LOGIN(callbackUrl);
    }
  };

  const handleGoToFreeTrial = (): void => {
    // 前往 Free trial 表單
    const { partNumber } = props.planData;
    router.push(
      `/${locale.value}/marketplace/pricing/${route.params.id}/${partNumber}/free-trial`
    );
  };

  const handleGoToContactSales = (): void => {
    // 前往 Contact sales 表單
    const { partNumber } = props.planData;
    router.push(
      `/${locale.value}/marketplace/pricing/${route.params.id}/${partNumber}/contact-sales`
    );
  };

  const handleGoToProductCheckout = (): void => {
    // 前往 Console Product Checkout
    const { partNumber } = props.planData;
    window.location.href = `${consoleDomain.value}/${locale.value}/checkout/product-purchase?partNumber=${partNumber}&urlAlias=${route.params.id}`;
  };

  const handleGoToLicenseUpgrade = (): void => {
    // 前往 Console License Upgrade
    const { partNumber } = props.planData;
    window.location.href = `${consoleDomain.value}/${locale.value}/checkout/license-upgrade?partNumber=${partNumber}&urlAlias=${route.params.id}`;
  };

  const handleSetDataCenter = (): void => {
    // 跳出 EnSaaS Data Center 彈窗，導向 EnSaaS
    const { partNumber } = props.planData;
    dataCenter.value.partNumber = partNumber;
    dataCenter.value.isShowPopUp = true;
  };

  const handleClick = (): void => {
    const { isShowPrice } = props.planData;

    switch (conditionType.value) {
      case 'A':
        return handleGoToFreeTrial();
      case 'B':
        return isLogin.value ? handleGoToProductCheckout() : handleLogin();
      case 'C':
      case 'E':
        return isLogin.value ? handleSetDataCenter() : handleLogin();
      case 'D':
        return handleGoToContactSales();
      case 'F':
      case 'H':
        return isLogin.value
          ? isShowPrice
            ? handleGoToProductCheckout()
            : handleGoToContactSales()
          : handleLogin();
      case 'G':
        return isLogin.value
          ? isShowPrice
            ? handleGoToLicenseUpgrade()
            : handleGoToContactSales()
          : handleLogin();
      default:
        return;
    }
  };
</script>

<i18n lang="json">
{
  "en-int": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "per-year": "per year",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "en-jp": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "per-year": "per year",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "en-tw": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "per-year": "per year",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "en-kr": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "per-year": "per year",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "en-americas": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "per-year": "per year",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "en-asia-pacific": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "per-year": "per year",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "en-emea": {
    "free-trial": "Free Trial",
    "floating-price": "Floating Price",
    "get-started": "Get started",
    "per-month": "per month",
    "subscribe": "Subscribe",
    "purchase": "Purchase",
    "per-license": "per license",
    "login-to-see-price": "Login to see price"
  },
  "ja-jp": {
    "free-trial": "無料トライアル",
    "floating-price": "変動価格",
    "get-started": "はじめる",
    "per-month": "月額",
    "per-year": "年額",
    "subscribe": "購読",
    "purchase": "購入",
    "per-license": "ライセンスごと",
    "login-to-see-price": "価格はログイン後に表示"
  },
  "zh-cn": {
    "free-trial": "免费试用",
    "floating-price": "浮动价格",
    "get-started": "开始使用",
    "per-month": "每月",
    "per-year": "每年",
    "subscribe": "订阅",
    "purchase": "购买",
    "per-license": "每个许可证",
    "login-to-see-price": "登录显示价格"
  },
  "zh-tw": {
    "free-trial": "免費試用",
    "floating-price": "浮動價格",
    "get-started": "開始使用",
    "per-month": "每月",
    "per-year": "每年",
    "subscribe": "訂閱",
    "purchase": "購買",
    "per-license": "每個授權",
    "login-to-see-price": "登入顯示價格"
  }
}
</i18n>
